import { Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import LoadingButton from '../../../../components/@extended/LoadingButton';
import FlowSummary from '../../../../components/ui/FlowSummary';
import { focusRingSx } from '../../../../components/ui/styleKit';
import { useSimulateActions, useSimulateMeta, useSimulateResult, useSimulateState } from '../SimulateTradeContext';
import { MARKET_KIND, splitOrderType } from '../constants';
import { SIMULATE_COPY } from '../content';
import { formatNumber } from '../format';
import { simulatePrimaryActionSx } from '../styles';

const { invalid, actions, form } = SIMULATE_COPY;
const REASON_ID = 'simulate-blocked-reason';

/** Dialog footer: the order read-back (or why Simulate is blocked), Reset and the Simulate action. */
export const SimulateActionBar = () => {
  const theme = useTheme();
  const { masterOrder } = useSimulateState();
  const { masterAccountId, slaveAccountId } = useSimulateMeta();
  const { loading } = useSimulateResult();
  const { submit, resetForm } = useSimulateActions();

  const checks: [boolean, string][] = [
    [!masterAccountId || !slaveAccountId, invalid.accounts],
    [!masterOrder.symbol.trim(), invalid.symbol],
    [!(masterOrder.lots > 0), invalid.lots]
  ];
  const blockedReason = checks.find(([failed]) => failed)?.[1] ?? '';

  const { side, kind } = splitOrderType(masterOrder.orderType);
  const market = kind === MARKET_KIND;
  const price = market ? SIMULATE_COPY.marketPrice : masterOrder.openPrice ? formatNumber(masterOrder.openPrice) : SIMULATE_COPY.empty;
  const summary = SIMULATE_COPY.summary(form.sides[side], market ? '' : form.kinds[kind], masterOrder.lots, masterOrder.symbol, price);

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, color: 'text.secondary' }}>
        {blockedReason ? (
          <>
            <InfoCircleOutlined style={{ fontSize: 13, flexShrink: 0 }} />
            <Typography id={REASON_ID} variant="caption" noWrap>
              {blockedReason}
            </Typography>
          </>
        ) : (
          <FlowSummary text={summary} />
        )}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <Button variant="text" color="inherit" onClick={resetForm} disabled={loading} sx={focusRingSx(theme)}>
          {actions.reset}
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          loadingPosition="end"
          endIcon={<ArrowRightOutlined />}
          disabled={Boolean(blockedReason) || loading}
          aria-describedby={blockedReason ? REASON_ID : undefined}
          onClick={submit}
          sx={simulatePrimaryActionSx(theme)}
        >
          {actions.simulate}
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
